'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import type { NavItem } from '@/content/nav';
import type { Dictionary } from '@/i18n/dictionary';
import type { Locale } from '@/i18n/config';
import { LanguageSwitcher } from './language-switcher';
import { Wordmark } from '@/components/ui/logo';
import { ButtonLink } from '@/components/ui/button';

type MobileMenuProps = {
  dictionary: Dictionary;
  locale: Locale;
  items: NavItem[];
  onClose: () => void;
};

export function MobileMenu({ dictionary, locale, items, onClose }: MobileMenuProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={dictionary.menu.label}
      style={{ animationDuration: '0.3s' }}
      className="fixed inset-0 z-60 flex animate-fade-in flex-col overflow-auto bg-canvas px-gutter pt-3.5 pb-[clamp(24px,4vw,48px)]"
    >
      <div className="flex h-12 items-center justify-between">
        <Wordmark />
        <button
          type="button"
          onClick={onClose}
          className="inline-flex min-h-11 min-w-11 items-center rounded-full border border-line px-4 text-sm font-medium"
        >
          {dictionary.menu.close}
        </button>
      </div>

      <nav
        aria-label={dictionary.nav.main}
        className="mt-[clamp(32px,8vh,72px)] flex flex-col border-t border-line"
      >
        {items.map((item, index) => (
          <Link
            key={item.key}
            href={item.href}
            onClick={onClose}
            style={{
              animationDelay: `${(0.08 + index * 0.06).toFixed(2)}s`,
              animationDuration: '0.6s',
            }}
            className="flex animate-fade-up items-center justify-between border-b border-line py-4.5 text-[clamp(32px,7vw,48px)] font-light tracking-[-0.03em] transition-colors hover:text-accent"
          >
            <span>{item.label}</span>
            <span className="text-[13px] font-medium text-muted">{item.index}</span>
          </Link>
        ))}
      </nav>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-4 pt-8">
        <LanguageSwitcher
          locale={locale}
          label={dictionary.menu.language}
          size="md"
          className="bg-transparent"
        />
        <ButtonLink href="#contact" onClick={onClose} className="min-h-13">
          {dictionary.nav.cta}
        </ButtonLink>
      </div>
    </div>
  );
}
